import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { DELETE_CONTENT, DELETE_REPORT, GET_STORY_DETAILS } from "../../../api/url";
import Loader from "../../../components/common/Loader";
import { arrowBack, deleteIcon, ProfileImage } from "../../../assets/image";
import DeletModel from "../../model/DeletModel";
import DissmissReportModel from "../../../components/ReportComponents/DissmissReportModel";
import moment from "moment";
import { strings } from "../../../strings/strings";
import { toast } from "react-toastify";

const StoriesDetails = () => {
  const { mutate: deleteStory } = useAppMutation();
  const { mutate: dismissReport } = useAppMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const [results, setResults] = useState({});
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const fromReport = location?.state?.fromReport || false;
  const report = location?.state?.report || null;

  const { data, isLoading, refetch } = useAppQuery({
    queryKey: [QUERY_KEYS.STORY_DETAILS, location?.state?.storyId],
    url: `${GET_STORY_DETAILS}?storyId=${location.state?.storyId}`,
  });

  useEffect(() => {
    if (data?.results) {
      setResults(data.results);
    }
  }, [data]);

  const handleDeleteStory = (story) => {
    const contentId = story?.id || story?._id || story?.content?.id;

    if (!contentId) {
      toast.error(strings.invalidStoryId);
      return;
    }

    setSelectedStoryId(contentId);
    setShowModal(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedStoryId) {
      toast.error(strings.noStoryIdAvailable);
      return;
    }

    deleteStory(
      {
        url: `${DELETE_CONTENT}?contentId=${selectedStoryId}`,
        method: "DELETE",
      },
      {
        onSuccess: () => {
          toast.success(strings.containtDeletedSuccessfully);
          setShowModal(false);
          setSelectedStoryId(null);
          setTimeout(() => {
            navigate(-1);
          }, 1000);
        },
        onError: () => {
          toast.error(strings.failToDeleteContaint);
        },
      }
    );
  };

  const handleCancelDelete = () => {
    setShowModal(false);
    setSelectedStoryId(null);
  };

  const handleDismissReport = () => {
    setSelectedReportId(report?._id);
    setShowDismissModal(true);
  };

  const handleConfirmDismiss = () => {
    if (!selectedReportId) {
      toast.error(strings.NoReportSelected);
      return;
    }

    dismissReport(
      {
        url: `${DELETE_REPORT}?reportId=${selectedReportId}`,
        method: "DELETE",
      },
      {
        onSuccess: () => {
          toast.success(strings.reportDissmissSuccess);
          setShowDismissModal(false);
          setSelectedReportId(null);
          refetch();
          setTimeout(() => navigate(-1), 1000);
        },
        onError: () => {
          toast.error(strings.failToDissmissReport);
        },
      }
    );
  };

  const handleCancelDismiss = () => {
    setShowDismissModal(false);
    setSelectedReportId(null);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen">
      <div className="flex gap-2 mb-5">
        <button onClick={() => navigate(-1)}>
          <img src={arrowBack} alt="Back" />
        </button>
        <h2 className="text-xl sm:text-2xl font-semibold">Story details</h2>
      </div>

      <div className="max-w-4xl mx-auto rounded-xl overflow-hidden">
        <div className="flex justify-between items-center gap-5 mb-3">
          <h1 className="text-xl capitalize font-bold">{results?.tittle || "Untitled Story"}</h1>
          {fromReport ? (
            <div className="flex gap-3">
              <button onClick={handleDismissReport} className="text-primary font-semibold">
                {strings?.dismissReport}
              </button>
              <span className="text-gray-300">|</span>
              <button onClick={() => handleDeleteStory(results)} className="text-red-600 font-semibold">
                {strings?.DeleteStory}
              </button>
            </div>
          ) : (
            <button
              onClick={() => handleDeleteStory(results)} // Ensure this triggers the modal
            >
              <img src={deleteIcon} alt="Delete" className="w-6 h-6" />
            </button>
          )}
        </div>

        {results?.storyImage && (
          <div className="relative">
            <img src={results.storyImage} alt="Story" className="w-full h-auto object-cover rounded-lg" />
            <div className="flex my-6 items-center gap-3">
              <img
                src={results?.user?.profileImage || ProfileImage}
                alt={results?.user?.name}
                className="w-12 h-12 rounded-full"
              />

              <div className="grid">
                <p className="font-semibold">{results?.user?.userName}</p>
                <p className="text-sm text-light">{moment(results?.createdAt).fromNow()}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 prose max-w-none">
          {results ? (
            <>
              <p className="mb-2">
                {results.storyOrigin && (
                  <div>
                    <div dangerouslySetInnerHTML={{ __html: results.storyOrigin }} />
                  </div>
                )}
              </p>
              <p className="mb-2">
                {results.journey && (
                  <div>
                    <div dangerouslySetInnerHTML={{ __html: results.journey }} />
                  </div>
                )}
              </p>
              <p className="mb-2">

              {results.turningPoint && (
                <div>
                  <div dangerouslySetInnerHTML={{ __html: results.turningPoint }} />
                </div>
              )}
              </p>
              <p className="mb-2">

              {results.resolutionOutcome && (
                <div>
                  <div dangerouslySetInnerHTML={{ __html: results.resolutionOutcome }} />
                </div>
              )}
              </p>
              <p className="mb-2">

              {results.lessonImpact && (
                <div>
                  <div dangerouslySetInnerHTML={{ __html: results.lessonImpact }} />
                </div>
              )}
              </p>
            </>
          ) : (
            <p className="text-grey">{strings.noContaintAvailableForStory}</p>
          )}
        </div>
      </div>

      {showModal && <DeletModel handleConfirmDelete={handleConfirmDelete} handleCancelDelete={handleCancelDelete} />}
      {showDismissModal && (
        <DissmissReportModel handleconfirmDissmiss={handleConfirmDismiss} handleCancelDissmiss={handleCancelDismiss} />
      )}
    </div>
  );
};

export default StoriesDetails;
